import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, SxProps, TextField, Theme } from '@mui/material';
import DatePicker from 'react-datepicker';
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './carbonCareDatePicker.css';

const DATE_FORMAT = 'yyyy-MM-dd';
/** Lowercase so the hint reads as a mask rather than a literal value. */
const DATE_PLACEHOLDER = 'yyyy-mm-dd';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** yyyy-MM-dd in local time. Avoids toISOString()'s UTC shift. */
export const formatIsoDate = (date: Date | null | undefined): string => {
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** Strict yyyy-MM-dd parse, so half-typed input never becomes a stray date. */
export const parseIsoDate = (text: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text.trim());
  if (!m) return null;
  const [year, month, day] = [m[1], m[2], m[3]].map(Number);
  const date = new Date(year, month - 1, day);
  // Rejects overflow like 2026-02-31, which Date would roll into March.
  return date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // `new Date('2026-09-11')` is parsed as UTC midnight, which lands on the 10th
  // anywhere west of Greenwich. Date-only strings go through the local parser.
  const isoOnly = parseIsoDate(value);
  if (isoOnly) return isoOnly;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d: Date) =>
  new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d: Date) =>
  new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);
const sameDay = (a: Date | null, b: Date | null) =>
  !!a && !!b && formatIsoDate(a) === formatIsoDate(b);

const yearRange = (min?: Date, max?: Date): number[] => {
  const first = min ? min.getFullYear() : new Date().getFullYear() - 15;
  const last = max ? max.getFullYear() : new Date().getFullYear() + 15;
  return Array.from({ length: Math.max(last - first + 1, 1) }, (_, i) => first + i);
};

const clamp = (date: Date, min?: Date, max?: Date): Date => {
  if (min && date < startOfDay(min)) return startOfDay(min);
  if (max && date > startOfDay(max)) return startOfDay(max);
  return date;
};

/* ------------------------------------------------------------------ */
/* Calendar header: ‹ September 2026 ▾ › + month/year overlay          */
/* ------------------------------------------------------------------ */

/** Which grid the header overlay is showing, if any. */
type OverlayMode = 'none' | 'month' | 'year';

const ChevronIcon: React.FC<{ dir: 'left' | 'right' | 'down' }> = ({ dir }) => {
  const rotate = { left: 90, right: -90, down: 0 }[dir];
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"
      style={{ transform: `rotate(${rotate}deg)` }}>
      <path d="M6 9.5 12 15.5 18 9.5" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

interface CalendarHeaderProps {
  date: Date;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
  years: number[];
  overlay: OverlayMode;
  onOverlayChange: (mode: OverlayMode) => void;
}

/**
 * Replaces react-datepicker's native month/year <select>s. Native selects render
 * an OS-drawn dropdown that ignores the app theme entirely, so the month and year
 * live in a panel drawn over the day grid instead — the pattern Stripe and Google
 * Calendar use. It renders inside the calendar, so there is no nested popper to
 * fight for z-index or outside-click handling.
 */
const CalendarHeader: React.FC<CalendarHeaderProps> = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  years,
  overlay,
  onOverlayChange,
}) => {
  const month = date.getMonth();
  const year = date.getFullYear();
  const activeYearRef = useRef<HTMLButtonElement | null>(null);

  // Long year lists scroll; open on the selected year rather than at 15 years ago.
  useEffect(() => {
    if (overlay === 'year') activeYearRef.current?.scrollIntoView({ block: 'center' });
  }, [overlay]);

  const firstYear = years[0];
  const lastYear = years[years.length - 1];

  return (
    <div className="cc-dp__header">
      <button
        type="button"
        className="cc-dp__nav"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled || overlay !== 'none'}
        aria-label="Previous month"
      >
        <ChevronIcon dir="left" />
      </button>

      <button
        type="button"
        className="cc-dp__caption"
        onClick={() => onOverlayChange(overlay === 'none' ? 'month' : 'none')}
        aria-expanded={overlay !== 'none'}
        aria-label={`${MONTHS[month]} ${year}. Choose a different month or year`}
      >
        <span>{MONTHS[month]} {year}</span>
        <span className={`cc-dp__caption-chevron${overlay !== 'none' ? ' is-open' : ''}`}>
          <ChevronIcon dir="down" />
        </span>
      </button>

      <button
        type="button"
        className="cc-dp__nav"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled || overlay !== 'none'}
        aria-label="Next month"
      >
        <ChevronIcon dir="right" />
      </button>

      {overlay !== 'none' && (
        <div className="cc-dp__overlay" role="group" aria-label="Choose month and year">
          {overlay === 'month' ? (
            <>
              <div className="cc-dp__overlay-bar">
                <button
                  type="button"
                  className="cc-dp__nav"
                  onClick={() => changeYear(year - 1)}
                  disabled={year - 1 < firstYear}
                  aria-label="Previous year"
                >
                  <ChevronIcon dir="left" />
                </button>
                <button
                  type="button"
                  className="cc-dp__overlay-year"
                  onClick={() => onOverlayChange('year')}
                  aria-label={`${year}. Choose a different year`}
                >
                  {year}
                </button>
                <button
                  type="button"
                  className="cc-dp__nav"
                  onClick={() => changeYear(year + 1)}
                  disabled={year + 1 > lastYear}
                  aria-label="Next year"
                >
                  <ChevronIcon dir="right" />
                </button>
              </div>
              <div className="cc-dp__overlay-grid">
                {MONTHS_SHORT.map((short, index) => (
                  <button
                    key={short}
                    type="button"
                    className="cc-dp__overlay-cell"
                    aria-pressed={index === month}
                    aria-label={MONTHS[index]}
                    onClick={() => { changeMonth(index); onOverlayChange('none'); }}
                  >
                    {short}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="cc-dp__overlay-grid cc-dp__overlay-grid--years">
              {years.map((candidate) => (
                <button
                  key={candidate}
                  ref={candidate === year ? activeYearRef : undefined}
                  type="button"
                  className="cc-dp__overlay-cell"
                  aria-pressed={candidate === year}
                  onClick={() => { changeYear(candidate); onOverlayChange('month'); }}
                >
                  {candidate}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Range presets                                                       */
/* ------------------------------------------------------------------ */

export interface DateRangePreset {
  label: string;
  /** Resolved against `anchor` — today, or maxDate when that is earlier. */
  resolve: (anchor: Date) => [Date, Date];
}

/**
 * Catalogue of quick ranges, anchored on the latest selectable day so a maxDate
 * of "today" needs no special casing. Consumers pick a subset: a filter bar that
 * already offers YTD / MTD / L12M buttons should not repeat them in the rail.
 */
export const RANGE_PRESETS: Record<string, DateRangePreset> = {
  last7: { label: 'Last 7 days', resolve: (a) => [addDays(a, -6), a] },
  last14: { label: 'Last 14 days', resolve: (a) => [addDays(a, -13), a] },
  last30: { label: 'Last 30 days', resolve: (a) => [addDays(a, -29), a] },
  last90: { label: 'Last 90 days', resolve: (a) => [addDays(a, -89), a] },
  thisMonth: { label: 'This month', resolve: (a) => [startOfMonth(a), a] },
  lastMonth: {
    label: 'Last month',
    resolve: (a) => {
      const prev = new Date(a.getFullYear(), a.getMonth() - 1, 1);
      return [prev, endOfMonth(prev)];
    },
  },
  last3Months: {
    label: 'Last 3 months',
    resolve: (a) => [startOfMonth(new Date(a.getFullYear(), a.getMonth() - 2, 1)), a],
  },
  thisQuarter: { label: 'This quarter', resolve: (a) => [startOfQuarter(a), a] },
  lastQuarter: {
    label: 'Last quarter',
    resolve: (a) => {
      const prev = new Date(a.getFullYear(), startOfQuarter(a).getMonth() - 3, 1);
      return [prev, endOfQuarter(prev)];
    },
  },
  ytd: { label: 'Year to date', resolve: (a) => [new Date(a.getFullYear(), 0, 1), a] },
  last12Months: {
    label: 'Last 12 months',
    resolve: (a) => [addDays(new Date(a.getFullYear() - 1, a.getMonth(), a.getDate()), 1), a],
  },
  previousYear: {
    label: 'Previous year',
    resolve: (a) => [
      new Date(a.getFullYear() - 1, 0, 1),
      new Date(a.getFullYear() - 1, 11, 31),
    ],
  },
};

/** For pickers that stand alone, with no period buttons of their own beside them. */
export const DEFAULT_RANGE_PRESETS: DateRangePreset[] = [
  RANGE_PRESETS.last7,
  RANGE_PRESETS.last30,
  RANGE_PRESETS.thisMonth,
  RANGE_PRESETS.lastMonth,
  RANGE_PRESETS.last3Months,
  RANGE_PRESETS.ytd,
  RANGE_PRESETS.last12Months,
];

/**
 * For pickers sitting next to YTD / MTD / L12M / Last Month buttons — every entry
 * here is a range those buttons cannot already produce.
 */
export const COMPLEMENTARY_RANGE_PRESETS: DateRangePreset[] = [
  RANGE_PRESETS.last7,
  RANGE_PRESETS.last14,
  RANGE_PRESETS.last30,
  RANGE_PRESETS.last90,
  RANGE_PRESETS.thisQuarter,
  RANGE_PRESETS.lastQuarter,
];

interface PresetRailProps {
  presets: DateRangePreset[];
  anchor: Date;
  minDate?: Date;
  maxDate?: Date;
  fromDate: Date | null;
  toDate: Date | null;
  onApply: (range: [Date, Date]) => void;
}

const PresetRail: React.FC<PresetRailProps> = ({
  presets, anchor, minDate, maxDate, fromDate, toDate, onApply,
}) => (
  <div className="cc-dp__presets">
    <p className="cc-dp__presets-title">Quick ranges</p>
    {presets.map((preset) => {
      const [start, end] = preset.resolve(anchor);
      const range: [Date, Date] = [clamp(start, minDate, maxDate), clamp(end, minDate, maxDate)];
      const active = sameDay(fromDate, range[0]) && sameDay(toDate, range[1]);
      return (
        <button
          key={preset.label}
          type="button"
          className="cc-dp__preset"
          aria-pressed={active}
          onClick={() => onApply(range)}
        >
          {preset.label}
        </button>
      );
    })}
  </div>
);

/* ------------------------------------------------------------------ */
/* Range picker: two inputs, one shared calendar                       */
/* ------------------------------------------------------------------ */

const CalendarIcon: React.FC = () => (
  <svg className="cc-range__icon" viewBox="0 0 24 24" width="17" height="17"
    aria-hidden="true" focusable="false">
    <rect x="3.25" y="5" width="17.5" height="15.75" rx="2.75" fill="none"
      stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.25 9.75h17.5M8 3.25v3.5M16 3.25v3.5" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

interface RangeInputsProps {
  fromValue: string;
  toValue: string;
  fromLabel: string;
  toLabel: string;
  onFromText: (date: Date | null) => void;
  onToText: (date: Date | null) => void;
  onClear?: () => void;
  disabled?: boolean;
  onClick?: React.MouseEventHandler;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  onKeyDown?: React.KeyboardEventHandler;
}

/**
 * react-datepicker clones this and hands it the popup's open/close handlers.
 * Both fields get them, so either one opens the same calendar.
 *
 * Plain inputs rather than MUI TextFields: the control has to sit at the same
 * height as the `size="small"` buttons beside it, and a floating label plus
 * MUI's own padding cannot fit in that box without clipping the value.
 * The From / To names live on aria-label, and the arrow carries the direction.
 */
const RangeInputs = forwardRef<HTMLDivElement, RangeInputsProps>(function RangeInputs(
  {
    fromValue, toValue, fromLabel, toLabel, onFromText, onToText, onClear,
    disabled, onClick, onFocus, onBlur, onKeyDown,
  },
  ref,
) {
  const [focused, setFocused] = useState(false);
  // Local text so a partially typed date isn't fought by the controlled value.
  const [fromText, setFromText] = useState(fromValue);
  const [toText, setToText] = useState(toValue);
  useEffect(() => setFromText(fromValue), [fromValue]);
  useEffect(() => setToText(toValue), [toValue]);

  const typeInto =
    (setText: (v: string) => void, commit: (d: Date | null) => void) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setText(raw);
      if (raw.trim() === '') {
        commit(null);
        return;
      }
      const parsed = parseIsoDate(raw);
      if (parsed) commit(parsed);
    };

  const fieldProps = {
    className: 'cc-range__field',
    type: 'text' as const,
    inputMode: 'numeric' as const,
    autoComplete: 'off',
    spellCheck: false,
    maxLength: 10,
    placeholder: DATE_PLACEHOLDER,
    disabled,
    onClick,
    onKeyDown,
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => { setFocused(true); onFocus?.(e); },
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => { setFocused(false); onBlur?.(e); },
  };

  const showClear = !disabled && !!onClear && (!!fromValue || !!toValue);

  return (
    <div
      ref={ref}
      className={[
        'cc-range-input',
        focused ? 'cc-range-input--focused' : '',
        disabled ? 'cc-range-input--disabled' : '',
      ].filter(Boolean).join(' ')}
    >
      <CalendarIcon />
      <input
        {...fieldProps}
        aria-label={fromLabel}
        title={fromLabel}
        value={fromText}
        onChange={typeInto(setFromText, onFromText)}
      />
      <span className="cc-range__arrow" aria-hidden="true">
        <ChevronIcon dir="right" />
      </span>
      <input
        {...fieldProps}
        aria-label={toLabel}
        title={toLabel}
        value={toText}
        onChange={typeInto(setToText, onToText)}
      />
      {showClear && (
        <button
          type="button"
          className="cc-range__clear"
          aria-label="Clear date range"
          // Keep the popup shut: react-datepicker opens on the wrapper's click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => { e.stopPropagation(); onClear?.(); }}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
            <path d="M6.5 6.5l11 11m0-11l-11 11" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </div>
  );
});

export interface CarbonCareDateRangePickerProps {
  fromDate: Date | null;
  toDate: Date | null;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  fromLabel?: string;
  toLabel?: string;
  /** Quick-range rail beside the calendar. Pass [] to hide it. */
  presets?: DateRangePreset[];
  sx?: SxProps<Theme>;
}

const CarbonCareDateRangePicker: React.FC<CarbonCareDateRangePickerProps> = ({
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  minDate,
  maxDate,
  disabled,
  fromLabel = 'From',
  toLabel = 'To',
  presets = DEFAULT_RANGE_PRESETS,
  sx,
}) => {
  const pickerRef = useRef<DatePicker>(null);
  const [overlay, setOverlay] = useState<OverlayMode>('none');
  const years = useMemo(() => yearRange(minDate, maxDate), [minDate, maxDate]);
  const anchor = useMemo(
    () => clamp(startOfDay(new Date()), minDate, maxDate),
    [minDate, maxDate],
  );

  // The calendar reports a new range before the parent re-renders with it, so
  // the close handler reads the latest pick from here rather than from props.
  const rangeRef = useRef({ start: fromDate, end: toDate });
  useEffect(() => {
    rangeRef.current = { start: fromDate, end: toDate };
  }, [fromDate, toDate]);

  const handleChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      const [start, end] = dates;
      rangeRef.current = { start, end };
      onFromDateChange(start);
      onToDateChange(end);
    },
    [onFromDateChange, onToDateChange],
  );

  // "From fills To when To is empty". On the calendar path this waits for close
  // so it doesn't cut the two-click range selection short; on the typed path
  // there is no second click to wait for, so it applies immediately.
  const mirrorInto = useCallback(
    (start: Date) => {
      rangeRef.current = { start, end: start };
      onToDateChange(start);
    },
    [onToDateChange],
  );

  const handleClose = useCallback(() => {
    setOverlay('none');
    const { start, end } = rangeRef.current;
    if (start && !end) mirrorInto(start);
  }, [mirrorInto]);

  const handleFromTyped = useCallback(
    (date: Date | null) => {
      rangeRef.current = { ...rangeRef.current, start: date };
      onFromDateChange(date);
      if (date && !rangeRef.current.end) mirrorInto(date);
    },
    [onFromDateChange, mirrorInto],
  );

  const handleToTyped = useCallback(
    (date: Date | null) => {
      rangeRef.current = { ...rangeRef.current, end: date };
      onToDateChange(date);
    },
    [onToDateChange],
  );

  const handleClear = useCallback(() => {
    rangeRef.current = { start: null, end: null };
    onFromDateChange(null);
    onToDateChange(null);
  }, [onFromDateChange, onToDateChange]);

  const applyPreset = useCallback(
    ([start, end]: [Date, Date]) => {
      rangeRef.current = { start, end };
      onFromDateChange(start);
      onToDateChange(end);
      setOverlay('none');
      pickerRef.current?.setOpen(false);
    },
    [onFromDateChange, onToDateChange],
  );

  const renderHeader = useCallback(
    (props: ReactDatePickerCustomHeaderProps) => (
      <CalendarHeader
        {...props}
        years={years}
        overlay={overlay}
        onOverlayChange={setOverlay}
      />
    ),
    [years, overlay],
  );

  return (
    <Box sx={sx}>
      <DatePicker
        ref={pickerRef}
        selectsRange
        startDate={fromDate ?? undefined}
        endDate={toDate ?? undefined}
        onChange={handleChange}
        onCalendarClose={handleClose}
        minDate={minDate}
        maxDate={maxDate}
        disabled={disabled}
        dateFormat={DATE_FORMAT}
        popperPlacement="bottom-start"
        popperClassName="cc-datepicker-popper"
        showPopperArrow={false}
        renderCustomHeader={renderHeader}
        customInput={
          <RangeInputs
            fromValue={formatIsoDate(fromDate)}
            toValue={formatIsoDate(toDate)}
            fromLabel={fromLabel}
            toLabel={toLabel}
            onFromText={handleFromTyped}
            onToText={handleToTyped}
            onClear={handleClear}
          />
        }
      >
        {presets.length > 0 && (
          <PresetRail
            presets={presets}
            anchor={anchor}
            minDate={minDate}
            maxDate={maxDate}
            fromDate={fromDate}
            toDate={toDate}
            onApply={applyPreset}
          />
        )}
      </DatePicker>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Single-date picker                                                  */
/* ------------------------------------------------------------------ */

export interface CarbonCareDatePickerProps {
  value: Date | string | null | undefined;
  /**
   * Receives yyyy-MM-dd, or '' when the field is cleared. With `showTime` the
   * value carries a time too, so it arrives as a full ISO string instead.
   */
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  minDate?: Date;
  maxDate?: Date;
  fullWidth?: boolean;
  startAdornment?: React.ReactNode;
  /** Adds the time column beside the calendar. Changes the onChange payload. */
  showTime?: boolean;
  /** Minutes between time slots. Only meaningful with `showTime`. */
  timeIntervals?: number;
  /** Narrows the selectable slots, e.g. to future times only. */
  filterTime?: (time: Date) => boolean;
  /** Blocks typing, so the value can only come from the calendar. */
  readOnly?: boolean;
  placeholder?: string;
  /** Renders the popup in this element — for dialogs that would clip it. */
  portalId?: string;
  sx?: SxProps<Theme>;
}

export const CarbonCareDatePicker: React.FC<CarbonCareDatePickerProps> = ({
  value,
  onChange,
  label,
  required,
  disabled,
  error,
  helperText,
  minDate,
  maxDate,
  fullWidth,
  startAdornment,
  showTime,
  timeIntervals = 15,
  filterTime,
  readOnly,
  placeholder,
  portalId,
  sx,
}) => {
  const selected = toDate(value);
  const [overlay, setOverlay] = useState<OverlayMode>('none');
  const years = useMemo(() => yearRange(minDate, maxDate), [minDate, maxDate]);

  const renderHeader = useCallback(
    (props: ReactDatePickerCustomHeaderProps) => (
      <CalendarHeader
        {...props}
        years={years}
        overlay={overlay}
        onOverlayChange={setOverlay}
      />
    ),
    [years, overlay],
  );

  const emit = (date: Date | null) => {
    if (!date) return onChange('');
    return onChange(showTime ? date.toISOString() : formatIsoDate(date));
  };

  return (
    <DatePicker
      selected={selected}
      onChange={emit}
      onCalendarClose={() => setOverlay('none')}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      showTimeSelect={showTime}
      timeIntervals={timeIntervals}
      timeFormat="HH:mm"
      timeCaption="Time"
      filterTime={filterTime}
      dateFormat={showTime ? `${DATE_FORMAT} HH:mm` : DATE_FORMAT}
      placeholderText={placeholder ?? (showTime ? `${DATE_PLACEHOLDER} hh:mm` : DATE_PLACEHOLDER)}
      popperPlacement="bottom-start"
      popperClassName="cc-datepicker-popper"
      portalId={portalId}
      // react-datepicker's wrapper is an inline-block with no width, so a
      // fullWidth field would otherwise size to a shrink-wrapped box, not the
      // grid column. `.date-picker-full-width` lives in src/index.css.
      wrapperClassName={fullWidth ? 'date-picker-full-width' : undefined}
      showPopperArrow={false}
      renderCustomHeader={renderHeader}
      customInput={
        <TextField
          fullWidth={fullWidth}
          label={label}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            ...(startAdornment ? { startAdornment } : {}),
            readOnly,
            // A read-only field is still a button for the calendar.
            sx: readOnly ? { cursor: 'pointer', '& input': { cursor: 'pointer' } } : undefined,
          }}
          sx={sx}
        />
      }
    />
  );
};

export default CarbonCareDateRangePicker;
